<script>
import { Scatter } from 'vue-chartjs'
import { parseISO} from 'date-fns';

function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function niceLinearAxisTicks(min, max) {
  const lo = toFiniteNumber(min);
  const hi = toFiniteNumber(max);
  if (lo == null && hi == null) return {};
  // Chart.js 2 força ticks.min/max como labels extras (71.8 em cima de 70). Só sugerir.
  if (lo == null) return { suggestedMax: hi };
  if (hi == null) return { suggestedMin: lo };

  const span = Math.max(Math.abs(hi - lo), Number.EPSILON);
  const roughStep = span / 7;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  const step = residual <= 1 ? magnitude
    : residual <= 2 ? 2 * magnitude
    : residual <= 5 ? 5 * magnitude
    : 10 * magnitude;

  return {
    suggestedMin: Math.floor(lo / step) * step,
    suggestedMax: Math.ceil(hi / step) * step,
    stepSize: step,
  };
}

function dropCrowdedTicks(scale, ticks) {
  const list = ticks && ticks.length ? ticks : (scale && scale.ticks);
  if (!Array.isArray(list) || list.length < 2) return list;

  const valueOf = (tick) => (tick && typeof tick === 'object' && 'value' in tick ? tick.value : tick);
  const values = list.map(valueOf);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const minGap = Math.abs(hi - lo) * 0.08;
  if (!(minGap > 0)) return list;

  const filtered = list.filter((tick, index) => {
    const value = values[index];
    if (value === lo || value === hi) return true;
    return Math.abs(value - lo) >= minGap && Math.abs(value - hi) >= minGap;
  });
  return filtered.length >= 2 ? filtered : list;
}

export default {
  name: "SmartCamChart",
  extends: Scatter,
  props: ["min_x", "max_x", "sets", "label", "min_y", "max_y", "camDates", "camManualDates", "bridgeDepartureDay", "dateAccommodation"],
  data() {
    const min_x = this.min_x 
    const max_x = this.max_x 
    const dates = this.camDates
    const manualDates = this.camManualDates
    const bridgeDepartureDay = this.bridgeDepartureDay
    const dateAccommodation = this.dateAccommodation

    /** A data exibida precisa acompanhar o dia do eixo, por isso é contada em UTC a partir do alojamento. */
    const dateForAxisDay = (axisDay) => {
      const isoDate = String(dateAccommodation || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
      const [year, month, day] = isoDate.split('-').map(Number);
      const utc = new Date(Date.UTC(year, month - 1, day + Math.round(Number(axisDay) || 0)));
      const tooltipDay = String(utc.getUTCDate()).padStart(2, '0');
      const tooltipMonth = String(utc.getUTCMonth() + 1).padStart(2, '0');
      return `${tooltipDay}/${tooltipMonth}/${utc.getUTCFullYear()}`;
    };

    const formatTooltipDate = (dateValue) => {
      const tooltipDate = parseISO(dateValue);
      const day = String(tooltipDate.getDate()).padStart(2, "0");
      const month = String(tooltipDate.getMonth() + 1).padStart(2, "0");
      const year = tooltipDate.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const getDataPoint = (tooltipItem, data) =>
      data.datasets[tooltipItem.datasetIndex]?.data[tooltipItem.index];

    const getPointX = (tooltipItem, data) => {
      const point = getDataPoint(tooltipItem, data);
      if (point != null && typeof point === "object" && point.x != null) {
        return Number(point.x);
      }
      return Number(tooltipItem.xLabel);
    };

    const getPointY = (tooltipItem, data) => {
      const point = getDataPoint(tooltipItem, data);
      if (point != null && typeof point === "object" && point.y != null) {
        return Number(point.y);
      }
      return Number(tooltipItem.yLabel);
    };

    const formatPointY = (tooltipItem, data, decimals = 2) => {
      const value = getPointY(tooltipItem, data);
      if (Number.isNaN(value)) return "—";
      return value.toFixed(decimals);
    };

    const isBridgeDeparturePoint = (tooltipItem, data) => {
      if (bridgeDepartureDay == null) return false;
      const label = data.datasets[tooltipItem.datasetIndex].label;
      const pointDay = getPointX(tooltipItem, data);
      return pointDay === Number(bridgeDepartureDay) && label === "SmartCamV3";
    };

    const getSeriesLabel = (tooltipItem, data) => {
      const label = data.datasets[tooltipItem.datasetIndex].label;
      if (label === "SmartCamV3" && isBridgeDeparturePoint(tooltipItem, data)) {
        return "Punto de despunte del lote";
      }
      return label;
    };

    const getSeriesColors = (label) => {
      if (label === "Esperado" || label === "GPD Esperado") {
        return { borderColor: "#171a5c", titleFontColor: "#171a5c" };
      }
      if (label === "SmartCamV3" || label === "Punto de despunte del lote") {
        return { borderColor: "red", titleFontColor: "red" };
      }
      if (label === "Durante despunte del lote") {
        return { borderColor: "#CD5C5C", titleFontColor: "#CD5C5C" };
      }
      if (label === "Manual") {
        return { borderColor: "#87165c", titleFontColor: "#87165c" };
      }
      return { borderColor: "#89c25b", titleFontColor: "#89c25b" };
    };
    
    return {
      weightData: {
        datasets: this.sets
      },
      options: {
        tooltips: {
          mode: "single",
          intersect: false,
          callbacks: {
            title: function(tooltipItems, data) {
              if (!tooltipItems || !tooltipItems.length) return "";
              const day = getPointX(tooltipItems[0], data);
              return `Andamento: dia ${day}`;
            },
            beforeLabel: function() {
              return "";
            },
            label: function(tooltipItem, data) {
              const label = getSeriesLabel(tooltipItem, data);
              const yValue = formatPointY(tooltipItem, data);
              if (label === "Losses") return `Animais [Unid]: ${yValue}`;
              if (label === "Uniformidade") return `${label}: ${yValue} %`;
              if (label === "SmartCam" || label === "Manual") return `Peso: ${yValue} kg`;
              return `${label}: ${yValue} kg`;
            },
            afterLabel: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              let tooltip_date;
              const point = getDataPoint(tooltipItem, data);

              const axisDate = dateForAxisDay(getPointX(tooltipItem, data));

              if (label === "Manual") {
                tooltip_date = new Date(manualDates[tooltipItem.index]);
              }
              else if (label === "Esperado" || label === "GPD Esperado") {
                if (axisDate) return `Data estimada: ${axisDate}`;
                const baseDate = dates && dates.length > 0 ? parseISO(dates[0]) : new Date();
                const alignedDay = getPointX(tooltipItem, data);
                const estimatedDate = new Date(baseDate.getTime());
                estimatedDate.setDate(baseDate.getDate() + alignedDay);
                return `Data estimada: ${formatTooltipDate(estimatedDate.toISOString())}`;
              }
              else if (axisDate) {
                return `Data: ${axisDate}`;
              }
              else if (point?.datetime_fetched) {
                return `Data: ${formatTooltipDate(point.datetime_fetched)}`;
              }
              else if (dates && dates[tooltipItem.index]) {
                tooltip_date = parseISO(dates[tooltipItem.index]);
              }

              if (label === "Esperado" || label === "GPD Esperado") return "";

              if (tooltip_date) {
                tooltip_date.setDate(tooltip_date.getDate());
                return `Data: ${formatTooltipDate(tooltip_date.toISOString())}`;
              }

              return "";
            },
            labelTextColor: function() {
              return "#1a1a1a";
            },
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleFontSize: 16,
          titleFontColor: "#89c25b",
          bodyFontColor: "#1a1a1a",
          bodyFontSize: 14,
          displayColors: false,
          borderColor: "#89c25b",
          borderWidth: 2,
          custom: function(tooltipModel) {
            tooltipModel.bodyFontColor = "#1a1a1a";

            const dataPoints = tooltipModel.dataPoints;
            if (!dataPoints || !dataPoints.length || !this._chart) return;

            const datasetLabel = this._chart.data.datasets[dataPoints[0].datasetIndex].label;
            const seriesLabel = getSeriesLabel(dataPoints[0], this._chart.data);
            const colors = getSeriesColors(seriesLabel === "Punto de despunte del lote" ? seriesLabel : datasetLabel);

            tooltipModel.borderColor = colors.borderColor;
            tooltipModel.titleFontColor = colors.titleFontColor;
          }
        },
        title: {
          display: true,
          text: "Curvas de crescimento",
          fontSize: 20
        },
        legend: {
          display: true,
          position: "top"
        },
        scales: {
          xAxes: [{
            ticks: {
              min: min_x,
              max: max_x,
            },
            scaleLabel: {
              display: true,
              labelString: 'Andamento [dias]',
              fontSize: 16
            },
            gridLines: {
              color: "rgba(120, 184, 67, .125)",
              display: true,
            }
          }],
          yAxes: [{
            afterBuildTicks: dropCrowdedTicks,
            ticks: {
              ...niceLinearAxisTicks(this.min_y, this.max_y),
              autoSkip: true,
              autoSkipPadding: 8,
            },
            scaleLabel: {
              display: true,
              labelString: this.label,
              fontSize: 16
            },
            gridLines: {
              color: "rgba(120, 184, 67, .125)",
              display: true,
            }
          }]
        },
        layout:{
          padding: {
            left: 0
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        stroke: {
          curve: 'straight'
        },
      }
    }
  },
  mounted () {
    this.applyYAxisTicks();
    this.renderChart(this.weightData, this.options)
  },
  watch: {
    min_y() { this.refreshChart(); },
    max_y() { this.refreshChart(); },
  },
  methods: {
    applyYAxisTicks() {
      const yAxis = this.options && this.options.scales && this.options.scales.yAxes && this.options.scales.yAxes[0];
      if (!yAxis) return;
      yAxis.afterBuildTicks = dropCrowdedTicks;
      yAxis.ticks = {
        ...niceLinearAxisTicks(this.min_y, this.max_y),
        autoSkip: true,
        autoSkipPadding: 8,
      };
    },
    refreshChart() {
      this.applyYAxisTicks();
      this.renderChart(this.weightData, this.options);
    },
  },
}
</script>
